import React, { Fragment, memo, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import htm from 'htm';
import { formatInspectorValue, extractValidation } from './inspector-utils.js';
import { buildEventSections } from './inspector-builder.js';
import { buildEnhancedEventSections } from './inspector-builder-enhanced.js';
import {
  CollapsibleSection,
  ProductCards,
  ClassificationTags,
  AnalysisSummary,
  ChatTranscript,
  CopyableValue,
  LocationCard,
  ParticipantsList,
  DefinitionGrid,
  NormalizationTable
} from './inspector-components-enhanced.js';

const html = htm.bind(React.createElement);

// Tab navigation for Inspector views
export function InspectorTabs({ view, onChange, hasDeveloperInfo }) {
  const tabs = [
    { id: 'details', label: 'Details' },
    { id: 'raw', label: 'Raw JSON' },
  ];

  if (hasDeveloperInfo) {
    tabs.push({ id: 'developer', label: 'Alterations' });
  }

  return html`
    <div className="as-tab-bar" role="tablist" aria-label="Event payload views">
      ${tabs.map((tab) => {
        const active = view === tab.id;
        return html`<button
          type="button"
          className="as-tab ${active ? 'active' : ''}"
          role="tab"
          aria-selected=${active}
          onClick=${() => onChange(tab.id)}
          >
          ${tab.label}
        </button>`;
      })}
    </div>
  `;
}

// Unified section component for all data types
export function UnifiedSection({ section }) {
  const sectionClass = ['as-inspector-section', section.className]
    .filter(Boolean)
    .join(' ');

  // Render header matching existing style
  const renderHeader = () =>
    html`<div className="as-section-header">
      <h4>${section.title}</h4>
    </div>`;

  // Handle definition lists (key-value pairs)
  if (section.type === 'definition' && section.items) {
    const hasValues = section.items.some(item => item.value && item.value !== '—');
    if (!hasValues) return null;

    return html`
      <section className=${sectionClass}>
        ${renderHeader()}
        <dl>
          ${section.items.map((item, index) =>
            item.value && item.value !== '—' ? html`
              <${Fragment} key=${index}>
                <dt>${item.label}</dt>
                <dd>${item.value}</dd>
              </${Fragment}>
            ` : null
          )}
        </dl>
      </section>
    `;
  }

  // Handle compact lists
  if (section.type === 'list' && section.items) {
    if (!section.items.length) return null;

    return html`
      <section className=${sectionClass}>
        ${renderHeader()}
        <ul className="as-list">
          ${section.items.map((item, index) => html`
            <li key=${index}>${item}</li>
          `)}
        </ul>
      </section>
    `;
  }

  // Handle tags (sentiment, classification labels)
  if (section.type === 'tags' && section.items) {
    if (!section.items.length) return null;

    return html`
      <section className=${sectionClass}>
        ${renderHeader()}
        <div className="as-tag-list">
          ${section.items.map((item, index) => html`
            <span className="as-tag" key=${index}>
              ${item.label}
              ${item.detail ? html`<span className="as-tag-detail">${item.detail}</span>` : null}
            </span>
          `)}
        </div>
      </section>
    `;
  }

  // Handle tables
  if (section.type === 'table' && section.headers && section.rows) {
    if (!section.rows.length) return null;

    // Identify columns that have at least one non-empty value
    const hasData = section.headers.map((_, colIndex) =>
      section.rows.some(row => {
        const cell = row[colIndex];
        return cell !== null && cell !== undefined && cell !== '' && cell !== '—';
      })
    );

    // Filter headers and columns to only show non-empty ones
    const filteredHeaders = section.headers.filter((_, index) => hasData[index]);
    const filteredRows = section.rows.map(row =>
      row.filter((_, index) => hasData[index])
    );

    // Don't render if no columns have data
    if (filteredHeaders.length === 0) return null;

    return html`
      <section className=${sectionClass}>
        ${renderHeader()}
        <div className="as-table-wrapper">
          <table>
            <thead>
              <tr>
                ${filteredHeaders.map((header, index) => html`
                  <th key=${index}>${header}</th>
                `)}
              </tr>
            </thead>
            <tbody>
              ${filteredRows.map((row, rowIndex) => html`
                <tr key=${rowIndex}>
                  ${row.map((cell, cellIndex) => html`
                    <td key=${cellIndex}>${cell || '—'}</td>
                  `)}
                </tr>
              `)}
            </tbody>
          </table>
        </div>
      </section>
    `;
  }

  // Handle commerce summary
  if (section.type === 'commerce') {
    return html`
      <section className=${sectionClass}>
        ${renderHeader()}
        <div className="as-commerce-summary">
          ${section.total ? html`
            <span className="as-commerce-total">
              ${section.currency || 'USD'} ${section.total}
            </span>
          ` : null}
          ${section.orderId ? html`
            <span className="as-commerce-order">Order: ${section.orderId}</span>
          ` : null}
        </div>
      </section>
    `;
  }

  return null;
}

// Raw JSON viewer component
export const RawJsonViewer = memo(function RawJsonViewer({ raw, searchQuery }) {
  const jsonString = useMemo(() => JSON.stringify(raw, null, 2), [raw]);

  const highlightedJson = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) {
      return jsonString;
    }

    try {
      const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      const parts = jsonString.split(regex);
      return parts.map((part, i) =>
        regex.test(part)
          ? html`<mark key=${i} className="as-highlight">${part}</mark>`
          : part
      );
    } catch {
      return jsonString;
    }
  }, [jsonString, searchQuery]);

  return html`
    <pre className="as-raw-json">
      <code>${highlightedJson}</code>
    </pre>
  `;
});

// Developer view component for normalization changes
export const DeveloperView = memo(function DeveloperView({ raw }) {
  const normalizationData = useMemo(() => {
    const normalizationChanges = raw.properties?._normalization_changes;

    if (!normalizationChanges) {
      return null;
    }

    let changesList = null;

    if (typeof normalizationChanges === 'string') {
      try {
        changesList = JSON.parse(normalizationChanges);
      } catch (e) {
        changesList = normalizationChanges;
      }
    } else {
      changesList = normalizationChanges;
    }

    if (!changesList) {
      return null;
    }

    const parsedChanges = [];

    if (Array.isArray(changesList)) {
      changesList.forEach((change) => {
        if (typeof change === 'string') {
          // Parse format: "ACTION: old_path → new_path" or "ACTION: field → value (reason)"
          const match = change.match(/^([A-Z]+):\s*(.+?)\s*→\s*(.+)$/);
          if (match) {
            parsedChanges.push({
              action: match[1],
              from: match[2].trim(),
              to: match[3].trim()
            });
          }
        }
      });
    }

    if (parsedChanges.length === 0) {
      return null;
    }

    const grouped = {};
    parsedChanges.forEach(change => {
      if (!grouped[change.action]) {
        grouped[change.action] = [];
      }
      grouped[change.action].push(change);
    });

    return { changes: parsedChanges, grouped };
  }, [raw]);

  if (!normalizationData) {
    return html`
      <div className="as-developer-view-empty">
        <p className="as-empty-state">No alterations detected for this event.</p>
        <p className="as-empty-state-detail">Events are processed and normalized upon reception. Alterations will appear here if any fields were renamed, moved, or modified.</p>
      </div>
    `;
  }

  return html`
    <div className="as-developer-view">
      <div className="as-developer-view-header">
        <h4>Event Alterations</h4>
        <p className="as-developer-view-subtitle">
          ${normalizationData.changes.length} ${normalizationData.changes.length === 1 ? 'alteration' : 'alterations'} applied during event processing
        </p>
      </div>
      <${NormalizationTable} changes=${normalizationData.changes} grouped=${normalizationData.grouped} />
    </div>
  `;
});

// Export for backward compatibility
export const SummarySection = UnifiedSection;
export const PayloadSection = UnifiedSection;

// Resizable wrapper for Inspector
export function ResizableInspector(props) {
  const { isCollapsed, onCollapse } = props;
  const containerRef = useRef(null);
  const [width, setWidth] = useState(400);
  const [isResizing, setIsResizing] = useState(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseDown = useCallback((e) => {
    if (isCollapsed) return;
    e.preventDefault();
    setIsResizing(true);
    startX.current = e.clientX;
    startWidth.current = width;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  }, [width, isCollapsed]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const deltaX = startX.current - e.clientX;
      const newWidth = startWidth.current + deltaX;

      // Constrain width between 300px and 700px
      const constrainedWidth = Math.min(700, Math.max(300, newWidth));
      setWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  useEffect(() => {
    if (!isCollapsed) return;
    if (isResizing) {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }, [isCollapsed, isResizing]);

  if (isCollapsed) {
    return html`
      <aside className="as-panel as-panel-collapsed" aria-label="Inspector">
        <button
          type="button"
          className="as-collapse-toggle"
          onClick=${() => onCollapse(false)}
          aria-label="Expand inspector panel"
          aria-expanded="false"
        >
          ◀
        </button>
        <div className="as-collapsed-indicator" title="Inspector">
          <span style=${{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>Inspector</span>
        </div>
      </aside>
    `;
  }

  return html`
    <div
      ref=${containerRef}
      className="as-inspector-wrapper"
      style=${{
        width: `${width}px`,
        flexShrink: 0,
        position: 'relative'
      }}
    >
      <div
        className="as-resize-handle"
        onMouseDown=${handleMouseDown}
        onMouseEnter=${(e) => e.currentTarget.style.background = 'rgba(88, 166, 255, 0.1)'}
        onMouseLeave=${(e) => e.currentTarget.style.background = 'transparent'}
        style=${{
          position: 'absolute',
          left: '-5px',
          top: 0,
          bottom: 0,
          width: '10px',
          cursor: 'ew-resize',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          transition: 'background 0.2s ease'
        }}
      >
        <div style=${{
          width: '3px',
          height: '40px',
          background: isResizing ? 'var(--as-accent)' : 'var(--as-border)',
          borderRadius: '2px',
          opacity: isResizing ? 1 : 0.5,
          transition: 'all 0.2s ease'
        }} />
      </div>
      <${Inspector} ...${props} />
    </div>
  `;
}

// Enhanced section renderer using new components
export function EnhancedSection({ section }) {
  // Products
  if (section.type === 'products' && section.products && section.products.length > 0) {
    return html`
      <${CollapsibleSection}
        title=${section.title}
        icon=${section.icon}
        badge=${section.badge}
        defaultOpen=${section.defaultOpen !== false}
      >
        <${ProductCards} products=${section.products} />
      <//>
    `;
  }

  // Classification
  if (section.type === 'classification' && section.classifications) {
    // Pre-filter classifications to check if there are any valid values
    const validClassifications = section.classifications.filter(item => item.value && item.value.trim());
    if (validClassifications.length === 0) {
      return null; // Don't render empty classification sections
    }

    return html`
      <${CollapsibleSection}
        title=${section.title}
        icon=${section.icon}
        badge=${section.badge}
        defaultOpen=${section.defaultOpen !== false}
      >
        <${ClassificationTags} classifications=${section.classifications} />
      <//>
    `;
  }

  // Analysis
  if (section.type === 'analysis' && section.analyses && section.analyses.length > 0) {
    return html`
      <${CollapsibleSection}
        title=${section.title}
        icon=${section.icon}
        badge=${section.badge}
        defaultOpen=${section.defaultOpen !== false}
      >
        <${AnalysisSummary} analyses=${section.analyses} />
      <//>
    `;
  }

  // Chat transcript
  if (section.type === 'chat' && section.content) {
    return html`
      <${CollapsibleSection}
        title=${section.title}
        icon=${section.icon}
        defaultOpen=${section.defaultOpen !== false}
      >
        <${ChatTranscript} content=${section.content} />
      <//>
    `;
  }

  // Location
  if (section.type === 'location' && section.location) {
    return html`
      <${CollapsibleSection}
        title=${section.title}
        icon=${section.icon}
        defaultOpen=${section.defaultOpen !== false}
      >
        <${LocationCard} location=${section.location} />
      <//>
    `;
  }

  // Participants
  if (section.type === 'participants' && section.participants && section.participants.length > 0) {
    return html`
      <${CollapsibleSection}
        title=${section.title}
        icon=${section.icon}
        badge=${section.badge}
        defaultOpen=${section.defaultOpen !== false}
      >
        <${ParticipantsList} participants=${section.participants} />
      <//>
    `;
  }

  // Definition grid (2-column layout)
  if (section.type === 'definition-grid' && section.items) {
    // Filter out items with no valid values
    const validItems = section.items.filter(item => {
      const value = item.value;
      return value !== null && value !== undefined && value !== '' && value !== '—';
    });

    if (validItems.length === 0) {
      return null; // Don't render empty definition grid sections
    }

    // Add copyable values support
    const items = validItems.map(item => {
      if (item.copyable) {
        return {
          label: item.label,
          value: html`<${CopyableValue} value=${item.value} label=${item.label} />`
        };
      }
      return item;
    });

    return html`
      <${CollapsibleSection}
        title=${section.title}
        icon=${section.icon}
        badge=${section.badge}
        defaultOpen=${section.defaultOpen !== false}
      >
        <${DefinitionGrid} items=${items} />
      <//>
    `;
  }

  // Tags
  if (section.type === 'tags' && section.items && section.items.length > 0) {
    return html`
      <${CollapsibleSection}
        title=${section.title}
        icon=${section.icon}
        defaultOpen=${section.defaultOpen !== false}
      >
        <div className="as-tag-cloud">
          ${section.items.map((item, idx) => html`
            <span key=${idx} className="as-tag">
              ${item.label}
              ${item.detail && html`<span className="as-tag-detail">${item.detail}</span>`}
            </span>
          `)}
        </div>
      <//>
    `;
  }

  // Normalization Table (Developer Info)
  if (section.type === 'normalization-table' && section.changes && section.changes.length > 0) {
    return html`
      <${CollapsibleSection}
        title=${section.title}
        icon=${section.icon}
        defaultOpen=${section.defaultOpen !== false}
      >
        <${NormalizationTable} changes=${section.changes} grouped=${section.grouped} />
      <//>
    `;
  }

  // Fallback to original UnifiedSection for other types
  return html`<${UnifiedSection} section=${section} />`;
}

// Main Inspector component with cleaner layout
export function Inspector({ event, view, onViewChange, onCopy, hasEvents, onCollapse }) {
  if (!event) {
    const emptyMessage = hasEvents
      ? 'No event selected'
      : 'No events yet';

    return html`
      <aside className="as-inspector" aria-live="polite">
        <header className="as-inspector-header">
          <div className="as-inspector-header-content">
            <h3 className="as-inspector-title">Inspector</h3>
            <div className="as-inspector-actions">
              <button type="button" className="as-secondary" disabled>Copy</button>
              ${onCollapse ? html`
                <button
                  type="button"
                  className="as-collapse-toggle"
                  onClick=${() => onCollapse(true)}
                  aria-label="Collapse inspector panel"
                  aria-expanded="true"
                >
                  ▶
                </button>
              ` : null}
            </div>
          </div>
        </header>
        <p className="as-empty-state">${emptyMessage}</p>
      </aside>
    `;
  }

  const validation = useMemo(() => (event ? extractValidation(event.raw) : null), [event]);
  const detailSections = useMemo(() => (event ? buildEnhancedEventSections(event) : []), [event]);

  const eventTimestamp = event.raw.timestamp
    ? formatInspectorValue('timestamp', event.raw.timestamp)
    : '—';

  const eventId = event.raw.event_gid || event.raw.message_id || event.id;
  const eventType = event.eventType || event.raw.type || '—';
  const eventName = event.raw.event || event.name || '—';

  // Check if event has developer/normalization info
  const hasDeveloperInfo = useMemo(() => {
    return !!(event.raw.properties?._normalization_changes);
  }, [event.raw]);

  const shouldScroll = view !== 'raw' && view !== 'developer';

  return html`
    <aside className="as-inspector" aria-live="polite">
      <header className="as-inspector-header">
        <div className="as-inspector-header-content">
          <h3 className="as-inspector-title">Inspector</h3>
          <div className="as-inspector-actions">
            ${onCollapse ? html`
              <button
                type="button"
                className="as-collapse-toggle"
                onClick=${() => onCollapse(true)}
                aria-label="Collapse inspector panel"
                aria-expanded="true"
              >
                ▶
              </button>
            ` : null}
          </div>
        </div>

        <div className="as-inspector-event-info">
          <div className="as-event-primary">
            <span className="as-event-name">${eventName}</span>
            <span className="as-event-type">${eventType}</span>
          </div>
          <div className="as-event-meta">
            <span className="as-event-timestamp">${eventTimestamp}</span>
            ${eventId && eventId !== '—' ? html`
              <span className="as-event-id" title=${eventId}>${eventId.length > 20 ? eventId.substring(0, 17) + '...' : eventId}</span>
            ` : null}
          </div>
        </div>

        <div className="as-inspector-toolbar">
          <${InspectorTabs} view=${view} onChange=${onViewChange} hasDeveloperInfo=${hasDeveloperInfo} />
          <button
            type="button"
            className="as-copy-btn-icon"
            onClick=${onCopy}
            title="Copy JSON"
            aria-label="Copy event JSON to clipboard"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          </button>
        </div>
      </header>

      ${validation
        ? html`<div className="as-validation-banner" data-type=${validation.type}>${validation.message}</div>`
        : null}


      <div className="as-tab-panels ${shouldScroll ? 'as-scrollable' : ''}">
        <div className="as-tab-panel" hidden=${view !== 'details'}>
          ${detailSections.length
            ? html`<div className="as-schema-view as-enhanced-view">
                ${detailSections.map((section, index) => html`
                  <${EnhancedSection} key=${index} section=${section} />
                `)}
              </div>`
            : html`<p className="as-empty-state">No event data available.</p>`}
        </div>

        <div className="as-tab-panel" hidden=${view !== 'raw'}>
          <${RawJsonViewer} raw=${event.raw} searchQuery="" />
        </div>

        <div className="as-tab-panel" hidden=${view !== 'developer'}>
          <${DeveloperView} raw=${event.raw} />
        </div>
      </div>
    </aside>
  `;
}
